import * as documents from 'serviceWorker/db/documents';
import { documentEntityToModel } from 'serviceWorker/repository/document/documentEntityToModel';
import { DocumentEntity } from 'serviceWorker/repository/document/entity';
import { ensureAuthenticatedConfig } from 'serviceWorker/services/config';
import { fileKindService } from 'services/fileService/fileKindService';
import { apiUrl as apiUrlFromGlobalObject } from 'shared/apiUrl';
import { DOCUMENT_ERROR_SOURCE } from 'shared/domain/document/documentError';
import { isLinkedDocumentationSwLocalId } from 'shared/domain/document/documentLocalIds';
import {
  DocumentModel,
  DocumentParentQuery,
} from 'shared/domain/document/documentModel';
import type { DocumentUrlsRequest } from 'shared/domain/document/documentContract';
import {
  DocumentUrlsModel,
  DocumentUrlsResult,
} from 'shared/domain/document/documentUrlsModel';
import { isSyncStatusSuccess } from 'shared/domain/entitySyncStatus/syncStatus';
import { debugLog } from 'shared/logger/debugLog';
import { UploadStatus, isUploadStatusSuccess } from 'shared/types/uploadStatus';
import { isBlobUrl } from 'shared/utils/blobUrl';
import { createErrorWithCode } from 'shared/utils/errorHandling';
import { buildSharedDocumentDownloadUrl } from 'shared/download/sharedResourceDownloadUrls';
import {
  readSharedResourceSignedRequest,
  SharedDocumentSignedUrlRequest,
  SharedResourceSignedRequestResult,
} from 'shared/download/sharedResourceSignedRequest';
import {
  LOCAL_DOCUMENT_ERROR_CODES,
  LOCAL_DOCUMENT_ERROR_MESSAGES,
} from 'shared/utils/errorHandling/constants';
import { createAbortOnLogout } from 'serviceWorker/helpers/abortableServiceHandler';
import { pullPublicLinkResponse, SharedResourceResponse } from '../signedRequest';
import type { DocumentChanges } from 'serviceWorker/db/documents';
import { resolveDocumentRemoteSource } from './resolveDocumentUrl';

let documentAbortOnLogout: ReturnType<typeof createAbortOnLogout> | null = null;

function getLatestDrawingMergedUploadStatus(
  document: Pick<DocumentEntity, 'drawings'>
): UploadStatus | undefined {
  const drawings = document.drawings;
  if (!drawings || drawings.length === 0) {
    return undefined;
  }

  return drawings[drawings.length - 1]?.mergedUploadStatus;
}

function getDocumentsListByQuery(
  query: DocumentParentQuery
): Promise<DocumentModel[]> {
  return documents
    .getByQuery(query)
    .then((docs) => docs.map(documentEntityToModel));
}

export async function getDocument(id: number): Promise<DocumentModel> {
  const document = await documents.getOne(id);
  if (!document) {
    throw createErrorWithCode(
      `${LOCAL_DOCUMENT_ERROR_MESSAGES.NOT_FOUND_PREFIX} ${id}`,
      LOCAL_DOCUMENT_ERROR_CODES.LOCAL_DOCUMENT_NOT_FOUND
    );
  }
  return documentEntityToModel(document);
}

export function updateDocument(
  id: number,
  changes: DocumentChanges,
  options?: { skipModifyTime: boolean }
): Promise<number> {
  return documents.updateOne(id, changes, options);
}

async function getDocumentUrls(
  document: DocumentEntity,
  options?: { forceRefresh?: boolean }
): Promise<DocumentUrlsModel> {
  // Only fetch from backend when document is fully uploaded and synced.
  // _id/syncStatus are set early (once the document record itself is
  // created), well before the S3 upload is confirmed - calling downloadData
  // before uploadStatus is SUCCESS gets a 409 "incorrect upload status" from
  // the backend, since the file isn't there yet.
  const isFullyUploaded =
    document._id &&
    isSyncStatusSuccess(document.syncStatus) &&
    isUploadStatusSuccess(document.data?.uploadStatus);

  // Gallery "Refresh" after PDF upload: UI already has _id but IndexedDB may
  // still hold localData blob + stale uploadStatus. Must hit downloadData for
  // imageSignedRequest instead of returning blob: again.
  const shouldForceRemoteForRefresh =
    Boolean(options?.forceRefresh) &&
    Boolean(document._id) &&
    isSyncStatusSuccess(document.syncStatus);

  const useRemoteSource = isFullyUploaded || shouldForceRemoteForRefresh;

  if (useRemoteSource) {
    const setup = await ensureAuthenticatedConfig();

    if (!setup) {
      return Promise.reject(new Error('Cannot get config setup.'));
    }

    if (
      document.data?.isDrawn &&
      (!isUploadStatusSuccess(
        getLatestDrawingMergedUploadStatus(document)
      ) ||
        !isSyncStatusSuccess(document.drawingSyncStatus))
    ) {
      const signedRequest = await resolveDocumentRemoteSource(
        setup,
        document,
        options
      );

      if (!signedRequest.signedRequest && !document.data?.mergedSrc) {
        return Promise.reject(
          new Error('Document source data is undefined.')
        );
      }

      // Prefer S3 URL from backend over local blob URL
      // Blob URLs become invalid after page refresh, so we should use S3 URL when available
      const isConvertedPdf =
        fileKindService.isPdf(document) &&
        Boolean(signedRequest.imageSignedRequest);
      const localMergedSrc = document.data?.mergedSrc;
      const isLocalBlobUrl = isBlobUrl(localMergedSrc);
      const mergedSignedRequest = isConvertedPdf
        ? signedRequest.mergedSignedRequest ||
          signedRequest.drawingSignedRequest ||
          signedRequest.imageSignedRequest ||
          signedRequest.signedRequest ||
          (isLocalBlobUrl ? undefined : localMergedSrc)
        : signedRequest.mergedSignedRequest ||
          signedRequest.imageSignedRequest ||
          signedRequest.signedRequest ||
          (isLocalBlobUrl ? undefined : localMergedSrc);

      const localThumbnailMergedSrc = document.data?.thumbnailMergedSrc;
      const isLocalThumbnailBlobUrl = isBlobUrl(localThumbnailMergedSrc);
      const mergedThumbnailSignedRequest =
        signedRequest.mergedThumbnailSignedRequest ||
        (isConvertedPdf
          ? signedRequest.drawingSignedRequest ||
            signedRequest.imageSignedRequest
          : signedRequest.thumbnailSignedRequest ||
            signedRequest.imageSignedRequest) ||
        signedRequest.signedRequest ||
        (isLocalThumbnailBlobUrl ? undefined : localThumbnailMergedSrc);

      return Promise.resolve({
        signedRequest: signedRequest.signedRequest,
        thumbnailSignedRequest: signedRequest.thumbnailSignedRequest,
        imageSignedRequest: signedRequest.imageSignedRequest,
        mergedSignedRequest,
        mergedThumbnailSignedRequest,
        drawingSignedRequest: signedRequest.drawingSignedRequest,
      });
    }

    return resolveDocumentRemoteSource(setup, document, options);
  } else {
    if (!document.localData) {
      return Promise.reject(
        new Error('Document source data is undefined.')
      );
    }

    if (document.data?.isDrawn) {
      if (!document.data.mergedSrc) {
        return Promise.reject(
          new Error('Document drawing source data is undefined.')
        );
      }

      return Promise.resolve({
        signedRequest: document.localData,
        mergedSignedRequest: document.data.mergedSrc,
        mergedThumbnailSignedRequest: document.data.thumbnailMergedSrc,
        drawingSignedRequest: document.data.drawingSrc,
      });
    } else {
      return Promise.resolve({
        signedRequest: document.localData,
      });
    }
  }
}

const DOCUMENT_ERROR_URLS: DocumentUrlsModel = {
  signedRequest: DOCUMENT_ERROR_SOURCE,
  mergedSignedRequest: DOCUMENT_ERROR_SOURCE,
  drawingSignedRequest: DOCUMENT_ERROR_SOURCE,
  mergedThumbnailSignedRequest: DOCUMENT_ERROR_SOURCE,
};

/** Documents of one parent (issue, event, inspection, template, ...). */
export function getDocumentsOfParent(
  parent: DocumentParentQuery
): Promise<DocumentModel[]> {
  return getDocumentsListByQuery(parent);
}

/**
 * Sources of a stored document. Sources the server cannot sign are
 * `DOCUMENT_ERROR_SOURCE`, which the page shows as a broken file.
 */
export async function getDocumentUrlsOfLocalId({
  localId,
  forceRefresh,
}: DocumentUrlsRequest): Promise<DocumentUrlsResult> {
  if (isLinkedDocumentationSwLocalId(localId)) {
    throw new Error(`Document ${localId} is linked documentation, which the worker does not store.`);
  }

  const document = await getDocument(localId);
  const documentUrls = await getDocumentUrls(document, { forceRefresh }).catch(
    () => DOCUMENT_ERROR_URLS
  );
  return { localId: document.localId, documentUrls };
}

function pullSharedDocumentResponse(
  request: SharedDocumentSignedUrlRequest
): Promise<SharedResourceResponse> {
  if (!request.issueId || !request.token || (!request.documentId && !request.linkedDocumentationId)) {
    return Promise.reject(
      new Error('Issue id, token, and document id or linked documentation id are required')
    );
  }

  return pullPublicLinkResponse(documentAbortOnLogout, (setup) =>
    buildSharedDocumentDownloadUrl({
      apiUrl: setup ? setup.api : apiUrlFromGlobalObject,
      ...request,
    })
  );
}

/** Signed URL of a document shared through a public issue link, or the status the server refused it with. */
export async function getSharedDocumentSignedUrl(
  request: SharedDocumentSignedUrlRequest
): Promise<SharedResourceSignedRequestResult> {
  const { statusCode, body } = await pullSharedDocumentResponse(request);
  return readSharedResourceSignedRequest(statusCode, body);
}

export const init = (): void => {
  // Concurrent: a new public link request must not abort one in flight.
  documentAbortOnLogout = createAbortOnLogout('concurrent');
};
